/**
 * URL / path scrubbing shared by the logger and the Sentry seam.
 *
 * Patient ids live in URL path segments (`/persons/<guid>`) and query strings
 * (`?mrn=…`, `?q=Jane+Doe`). This module masks ID-shaped segments to `:id` and
 * drops the query string so neither a console line nor a Sentry event ever
 * carries a raw identifier. Both sinks import from here so the rules exist once.
 */

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Mask an ID-shaped path segment (UUID, numeric id, long hex) → `:id`. */
function maskSegment(segment: string): string {
  if (segment === '') return segment;
  if (UUID_RE.test(segment)) return ':id';
  if (/^\d+$/.test(segment)) return ':id';
  if (/^[0-9a-f]{16,}$/i.test(segment)) return ':id';
  return segment;
}

/**
 * True when a string is URL/path-shaped and therefore worth scrubbing: an
 * absolute URL (`https://…`) or a root-relative path (`/persons/…`). Guards
 * `scrubUrl` from mangling ordinary message strings.
 */
export function looksLikeUrl(value: string): boolean {
  return value.startsWith('/') || /^[a-z][a-z0-9+.-]*:\/\//i.test(value);
}

/**
 * Strip the query string (may contain MRNs / names / person_ids) and mask
 * ID-shaped path segments. Preserves the origin for absolute URLs so the
 * endpoint shape stays useful.
 */
export function scrubUrl(raw: string): string {
  try {
    const parsed = new URL(raw, 'http://scrubbed.invalid');
    const path = parsed.pathname.split('/').map(maskSegment).join('/');
    const isAbsolute = /^[a-z][a-z0-9+.-]*:/i.test(raw);
    return isAbsolute ? `${parsed.origin}${path}` : path;
  } catch {
    return '[unparseable-url]';
  }
}
